/**
 * Site Navigation
 */

import delegate from 'delegate'

import { NAV_MOBILE_BREAKPOINT } from '../../config/options'
import state from '../../config/state'
import { on, trigger } from 'utils/events'
import * as tools from 'utils/tools'

const el = {
	container: document.querySelector('[data-js="masthead"]'),
	navWrapper: document.querySelector('[data-js="site-nav__wrap"]'),
}

const navState = {
	wrapperClientW: 0,
	wrapperScrollW: 0,
	isMobile: false,
	isDesktop: false,
	mobileNavOpen: false,
	subMenuOpen: false,
}

/**
 * True if either the mobile nav is displayed or a submenu is displayed.
 *
 * @returns {boolean}
 */
const navActive = () => navState.mobileNavOpen || navState.subMenuOpen

/**
 * Update the ARIA attributes for navigation when opened.
 *
 * @param triggerEl
 * @param containerEl
 */
const openAriaAttributes = (triggerEl, containerEl = '') => {
	if (containerEl) {
		containerEl.setAttribute('aria-hidden', 'false')
		containerEl.inert = false
	}

	triggerEl.setAttribute('aria-expanded', 'true')

	const showLabel = triggerEl.getAttribute('data-label-shown')
	if (showLabel) {
		triggerEl.setAttribute('aria-label', showLabel)
	}
}

/**
 * Update the ARIA attributes for navigation when closed.
 *
 * @param triggerEl
 * @param containerEl
 */
const closeAriaAttributes = (triggerEl, containerEl = '') => {
	if (containerEl) {
		containerEl.setAttribute('aria-hidden', 'true')
		containerEl.inert = true
	}

	triggerEl.setAttribute('aria-expanded', 'false')

	const hiddenLabel = triggerEl.getAttribute('data-label-hidden')
	if (hiddenLabel) {
		triggerEl.setAttribute('aria-label', hiddenLabel)
	}
}

/**
 * Open a primary nav submenu
 *
 * @param menuItem
 */
const openSubMenu = (menuItem) => {
	// Trigger an event to inform other scripts of the change.
	trigger({ event: 'ur_core/nav_active', native: false })

	menuItem.classList.add('nav-primary__list-item--is-active')

	el.container.classList.add('nav-primary__submenu--is-active')

	const toggle = menuItem.querySelector('[data-js="trigger-child-menu"]')

	openAriaAttributes(toggle)

	navState.subMenuOpen = true
}

/**
 * Close a primary nav submenu
 *
 * @param menuItem
 */
const closeSubMenu = (menuItem) => {
	menuItem.classList.remove('nav-primary__list-item--is-active')

	el.container.classList.remove('nav-primary__submenu--is-active')

	const toggle = menuItem.querySelector('[data-js="trigger-child-menu"]')

	closeAriaAttributes(toggle)

	navState.subMenuOpen = false
}

/**
 * Close all submenus in the primary nav
 */
const closeAllSubMenus = () => {
	el.subNavMenuItems.forEach((menuItem) => closeSubMenu(menuItem))
}

/**
 * Toggle a specific submenu in the primary nav.
 *
 * @param e
 */
const toggleSubMenu = (e) => {
	const toggle = e.delegateTarget
	const menuItem = tools.closest(toggle, '.nav-primary__list-item')
	if (menuItem.classList.contains('nav-primary__list-item--is-active')) {
		closeSubMenu(menuItem)
	} else {
		if (!navState.isMobile) {
			closeAllSubMenus()
		}
		openSubMenu(menuItem)
	}
}

/**
 * Open site navigation menu
 */
const openNav = () => {
	// Trigger an event to inform other scripts of the change.
	trigger({ event: 'ur_core/nav_active', native: false })

	// Display the site nav
	el.container.classList.add('masthead__nav--is-active')

	// add class to body
	$('body').add('body__nav--is-active')

	// Handle additional tasks for displaying the nav.
	openAriaAttributes(el.siteNavTrigger, el.siteNav)

	// Update the visual text label displayed on the nav trigger.
	// const visualLabel = el.siteNavTrigger.querySelector( 'span' );
	// visualLabel.innerHTML = visualLabel.getAttribute( 'data-label-shown' );

	// Set the nav open state.
	navState.mobileNavOpen = true
}

/**
 * Close site navigation menu
 */
const closeNav = () => {
	// Close the site nav.
	el.container.classList.remove('masthead__nav--is-active')

	// Handle additional tasks for displaying the nav.
	closeAllSubMenus()
	closeAriaAttributes(el.siteNavTrigger, el.siteNav)

	// Update the visual text label displayed on the nav trigger.
	// const visualLabel = el.siteNavTrigger.querySelector( 'span' );
	// visualLabel.innerHTML = visualLabel.getAttribute( 'data-label-hidden' );

	// Set the nav open state to false
	navState.mobileNavOpen = false
}

/**
 * Toggle the site nav on mobile
 */
const toggleSiteNav = () => {
	el.container.classList.contains('masthead__nav--is-active')
		? closeNav()
		: openNav()
}

/**
 * Handle closing the mobile nav or submenu based on which is active.
 */
const handleOutActions = () => {
	// Close the mobile nav
	if (el.container.classList.contains('masthead__nav--is-active')) {
		closeNav()
		el.siteNavTrigger.focus()
		return
	}

	// Close any open submenus.
	if (el.container.classList.contains('nav-primary__submenu--is-active')) {
		el.subNavMenuItems.forEach((menuItem) => {
			if (menuItem.classList.contains('nav-primary__list-item--is-active')) {
				closeSubMenu(menuItem)
				const toggle = menuItem.querySelector('[data-js="trigger-child-menu"]')
				toggle.focus()
			}
		})
	}
}

/**
 * Handle clicking outside the masthead to close the site nav for mobile and a submenu on desktop.
 *
 * @param e
 */
const handleClickOut = (e) => {
	if (!navActive()) {
		return
	}

	if (!tools.closest(e.target, '[data-js="masthead"]')) {
		handleOutActions()
	}
}

/**
 * Handle the escape key to close the site nav on mobile and submenu on desktop.
 *
 * @param e
 */
const handleEsc = (e) => {
	// Bail if the key press was not escape or the nav is not active.
	if (e.which !== 27 || !navActive()) {
		return
	}

	handleOutActions()
}

/**
 * Handle tabbing out of the site nav on mobile.
 *
 * @param e
 */
const handleTabOut = (e) => {
	// Bail if the key press was not tab or the nav is not active.
	if (e.which !== 9 || !navActive()) {
		return
	}

	// Mobile: close the whole site nav if we tab out of it.
	if (
		el.container.classList.contains('masthead__nav--is-active') &&
		!tools.closest(e.target, '[data-js="site-nav__wrap"]')
	) {
		closeNav()
		el.siteNavTrigger.focus()
		return
	}

	// Desktop: close the active sub menu if we tab out of it.
	if (el.container.classList.contains('nav-primary__submenu--is-active')) {
		const menuItem = tools.closest(e.target, '.nav-primary__list-item--depth-0')
		if (
			!menuItem ||
			!menuItem.classList.contains('nav-primary__list-item--is-active')
		) {
			closeAllSubMenus()
		}
	}
}

/**
 * Checks the viewport width against the navigation breakpoint.
 *
 * Returns true if the viewport is smaller than the breakpoint.
 *
 * @returns {boolean}
 */
const isMobileViewport = () => state.v_width < NAV_MOBILE_BREAKPOINT

/**
 * Actions required to switch the nav to desktop
 */
const showDesktopNav = () => {
	if (navState.isDesktop) {
		return
	}
	closeNav()
	el.siteNav.setAttribute('aria-hidden', 'false')
	el.siteNav.inert = false
	el.container.classList.remove('masthead__nav--is-active')
	navState.isMobile = false
	navState.isDesktop = true
}

/**
 * Actions required to switch the nav to mobile.
 */
const showMobileNav = () => {
	if (navState.isMobile) {
		return
	}
	closeNav()
	navState.isMobile = true
	navState.isDesktop = false
}

/**
 * Handle the masthead navigation fit functionality.
 *
 * The masthead nav layout is toggled between desktop & mobile based on both the viewport size
 * and the number of menu items that will fit in one row. If the viewport is too small,
 * or the combined menu items from primary & secondary menu locations don't fit in a single row
 * then the mobile nav is activated.
 */
const handleNavFit = () => {
	if (isMobileViewport()) {
		showMobileNav()
	} else if (!isMobileViewport()) {
		showDesktopNav()
	}
}

/**
 * Bind any even handlers this module utilizes.
 */
const bindEvents = () => {
	delegate(el.container, '[data-js="toggle-site-nav"]', 'click', toggleSiteNav)

	// Make sure toggle buttons are active.
	el.menuItemTriggers.forEach((trigger) => {
		trigger.inert = false
	})

	delegate(
		el.container,
		'[data-js="trigger-child-menu"]',
		'click',
		toggleSubMenu
	)

	on(document, 'keyup', handleEsc)
	on(document, 'keyup', handleTabOut)
	on(document, 'click', handleClickOut)
	on(document, 'ur_core/resize_executed', handleNavFit)
}

/**
 * Cache a number of elements we need for functionality.
 */
const cacheElements = () => {
	el.titleRow = el.container.querySelector('[data-js="title-row"]')
	el.siteNav = el.container.querySelector('[data-js="site-navigation"]')
	el.siteNavTrigger = el.container.querySelector('[data-js="toggle-site-nav"]')
	el.subNavMenuItems = tools.getNodes(
		'.nav-primary__list-item--depth-0.nav-primary__list-item--has-children',
		true,
		el.container,
		true
	)
	el.menuItemTriggers = tools.getNodes('trigger-child-menu', true, el.container)
}

/**
 * Determine if navigation has wrapped to two rows and add classes to elements in each row.
 */
const handleNavWrap = () => {
	const navWrapperHeight = el.navWrapper.clientHeight
	const navItems = Array.from(el.subNavMenuItems)

	if (navWrapperHeight >= 120) {
		navItems.forEach((item) => {
			item.classList.add('nav-item--wrapped')
			if (item.offsetTop > 0) {
				item.classList.add('nav-item--wrapped-row-2')
			} else {
				item.classList.remove('nav-item--wrapped-row-2')
			}
		})
	}
}

/**
 * Init this module.
 */
const init = () => {
	if (!el.container || !el.navWrapper) {
		return
	}
	cacheElements()
	bindEvents()
	handleNavFit()
	handleNavWrap()
}

// Run handleNavWrap on resize
window.addEventListener('resize', handleNavWrap)

export default init
